import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ClientSidebar from "../components/ClientSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { requestPasswordReset } from "@/lib/auth-flows";

export default function PerfilSeguridadPage() {
  const { t } = useTranslation("profile");
  const { user } = useAuth();
  const [twoFactor, setTwoFactor] = useState(false);
  // Was a fake form: three password inputs and a handler that only flipped a
  // "saved" pill, so people believed their password had changed when nothing
  // happened. There is no change-password endpoint, so this now uses the real
  // (and already working) emailed reset flow instead.
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendResetLink = async () => {
    if (!user?.email) return;
    setSending(true);
    setError(null);
    try {
      await requestPasswordReset(user.email);
      setSent(true);
    } catch {
      setError(t("profile.resetLinkError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-offwhite pt-14 md:pt-20 pb-12">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            <ClientSidebar active="security" />
            <div className="flex-1 min-w-0 space-y-6">
              {/* Change Password */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-bold text-charcoal">{t("profile.changePassword")}</h2>
                </div>
                <p className="text-sm text-gray-500">{t("profile.changePasswordDesc")}</p>
                {user?.email && <p className="text-sm font-medium text-charcoal mt-2">{user.email}</p>}
                {error && (
                  <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-3 rounded-xl mt-4">{error}</div>
                )}
                {sent ? (
                  <div className="bg-ocean/10 border border-ocean/30 text-ocean text-sm px-4 py-3 rounded-xl mt-4">
                    {t("profile.resetLinkSent")}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendResetLink}
                    disabled={sending || !user?.email}
                    className="mt-6 bg-charcoal text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-charcoal/90 transition-colors disabled:opacity-50"
                  >
                    {sending ? t("profile.sendingResetLink") : t("profile.sendResetLink")}
                  </button>
                )}
              </div>

              {/* 2FA */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-charcoal">{t("profile.twoFactor")}</h2>
                    <p className="text-sm text-gray-500">{t("profile.twoFactorDesc")}</p>
                  </div>
                  <button
                    onClick={() => setTwoFactor(!twoFactor)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${twoFactor ? "bg-ocean" : "bg-gray-200"}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${twoFactor ? "right-1" : "left-1"}`} />
                  </button>
                </div>
              </div>

              {/* Active Sessions */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8">
                <h2 className="text-lg font-bold text-charcoal mb-4">{t("profile.activeSessions")}</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ocean/10 text-ocean"><i className="ri-computer-line" /></div>
                      <div>
                        <p className="text-sm font-medium text-charcoal">{t("profile.currentDevice")}</p>
                        <p className="text-xs text-gray-400">{t("profile.currentDeviceInfo")}</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-ocean bg-ocean/10 px-2 py-1 rounded-full">{t("profile.activeNow")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}